import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { errMsg } from "@/lib/api";
import { invalidate } from "@/hooks/use-resource";
import { Skeleton } from "@/components/common";
import { BarAction, PageBar } from "@/components/layout/PageBar";
import { kapasitetApi } from "@/lib/kapasitet";
import { AvdelingerTab } from "@/components/plan/settings/AvdelingerTab";
import { RessurserTab } from "@/components/plan/settings/RessurserTab";
import { KalenderTab } from "@/components/plan/settings/KalenderTab";
import { ReglerTab } from "@/components/plan/settings/ReglerTab";
import { DemoTab } from "@/components/plan/settings/DemoTab";

const TABS = [["avdelinger", "Avdelinger"], ["ressurser", "Maskiner & fasiliteter"], ["kalender", "Kalender & unntak"], ["regler", "Sannsynlighet, terskler & fordeling"], ["demo", "Demo-data"]];
const KEYS = ["avdelinger", "ressurser", "unntak", "sannsynlighet", "terskler", "fordeling", "reservasjon_kun_ledelse", "reservasjon_dager", "standard_opphold_dogn"];
const pick = (s) => Object.fromEntries(KEYS.map((k) => [k, s[k]]));

/** Every number the plan relies on, with its formula, in one place – explained capacity. The plan itself has no settings. */
export default function PlanSettingsPage() {
  const [params, setParams] = useSearchParams();
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState(null);
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "avdelinger";
  const setTab = (k) => { const n = new URLSearchParams(params); if (k === "avdelinger") n.delete("tab"); else n.set("tab", k); setParams(n, { replace: true }); };
  const [saving, setSaving] = useState(false);
  const load = () => kapasitetApi.innstillinger().then((r) => { setS(r.data); setDraft(pick(r.data)); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const dirty = Boolean(s && draft && JSON.stringify(pick(s)) !== JSON.stringify(draft));
  const save = async () => {
    setSaving(true);
    try { const r = await kapasitetApi.lagre(draft); setS(r.data); setDraft(pick(r.data)); invalidate("/kapasitet"); window.dispatchEvent(new Event("projects:changed")); toast.success("Kapasitetsoppsettet er lagret"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const demoChanged = () => { load(); invalidate("/kapasitet"); };
  return (
    <div className="mx-auto w-full max-w-[1200px] px-5 pb-16 pt-5 sm:px-8" data-testid="plan-settings-page">
      <PageBar title="Plan & kapasitet" meta="Forklart kapasitet – formel, kilde og alder" back={{ to: "/settings", label: "Til innstillinger" }} testId="plan-settings-bar" action={dirty ? <BarAction primary onClick={save} testId="plan-settings-save"><Save className="h-4 w-4" /> {saving ? "Lagrer…" : "Lagre endringer"}</BarAction> : null} />
      {s && !s.can_write ? <div className="mt-3 inline-flex items-center gap-1 text-[12px] text-[hsl(var(--v-assumed))]"><Lock className="h-3 w-3" /> Lesevisning – redigeringsrett mangler</div> : null}
      <div className="mt-5 flex flex-wrap gap-1 border-b border-black/[0.07]" role="tablist" data-testid="plan-settings-tabs">
        {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn("-mb-px h-10 border-b-2 px-3 text-[13.5px] font-medium transition-colors", tab === k ? "border-[hsl(var(--navy))] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")} data-testid={`plan-settings-tab-${k}`}>{l}{k === "demo" && s?.demo_rader ? <span className="ml-1.5 rounded-full bg-[hsl(var(--v-assumed-bg))] px-1.5 font-mono text-[10.5px] text-[hsl(var(--v-assumed))]">{s.demo_rader}</span> : null}</button>)}
      </div>
      <div className="mt-5">
        {!draft ? <Skeleton className="h-64" /> : (
          <>
            {tab === "avdelinger" ? <AvdelingerTab draft={{ ...draft, ikke_planlagt: s.ikke_planlagt }} onChange={setDraft} /> : null}
            {tab === "ressurser" ? <RessurserTab draft={draft} onChange={setDraft} /> : null}
            {tab === "kalender" ? <KalenderTab draft={draft} onChange={setDraft} helligdager={s.helligdager} /> : null}
            {tab === "regler" ? <ReglerTab draft={draft} onChange={setDraft} /> : null}
            {tab === "demo" ? <DemoTab demoRader={s.demo_rader} canWrite={s.can_write} onChanged={demoChanged} /> : null}
          </>
        )}
      </div>
      {s?.history?.length ? (
        <details className="mt-8 text-[12px] text-muted-foreground" data-testid="plan-settings-history">
          <summary className="cursor-pointer select-none font-medium">Historikk ({s.history.length})</summary>
          <ul className="mt-1.5 space-y-1">{[...s.history].reverse().slice(0, 20).map((h, i) => <li key={i}><span className="font-mono text-[11px]">{(h.at || "").slice(0, 16).replace("T", " ")}</span> · {h.by?.name || "–"} · {h.summary}</li>)}</ul>
        </details>
      ) : null}
    </div>
  );
}
